import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TableFooter,
  Typography,
  Box,
  Modal,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import EditModal from "../Modal/EditModalForFDHoldings";
import TimeStamptoExp from "../../utils/timestampConverter";
import {
  HoldingsPageShell,
  HoldingsAddButton,
  SortableHeader,
  EmptyTableRow,
  centerBodyCellSx,
  centerHeadCellSx,
  gainLossCellSx,
  holdingsPaginationSx,
} from "./holdingsTableShared";
interface FD {
  fdId: number;
  id: number;
  noteficationId: number;
  schemeName: string;
  providerName: string;
  category: string;
  amount: number;
  fdStatus: number;
  transactionDate: string;
  investmentFrequency: string;
  interesetRate: number;
  grossInterest: number;
  transactions: [];
  endDate: number;
  investmentTenure: number;
  returnAmount: number;
  createdAt: string;
  type: string;
  daysLeft: number;
  userId: number;
  startDate: number;
  name: string;
  firstName: string;
  folioNo: string;
}

interface Props {
  data: FD[];
  totalPages: number; // Total number of users
  pageSize: number; // Current page size
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void; // Page change handler
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void; // Page size change handler
  currentPage: number; // Current page number
  fetchData: () => void;
}

interface TransactionsModalProps {
  open: boolean;
  onClose: () => void;
  transactions: {
    transactionDate: string;
    investedAmount: number;
    days: number;
  }[];
}
const TransactionsModal: React.FC<TransactionsModalProps> = ({
  open,
  onClose,
  transactions,
}) => {
  const formatTransactionDate = (dateString: any) => {
    if (dateString.includes("/")) {
      const [day, month, year] = dateString.split("/"); // Convert MM/DD/YYYY format to DD-MM-YYYY
      return `${day}-${month}-${year}`;
    }

    // Check if the date is already in DD-MM-YYYY format
    if (dateString.includes("-")) {
      const [day, month, year] = dateString.split("-");
      return `${day}-${month}-${year}`; // Keep as DD-MM-YYYY
    }

    return dateString; // If the format is unexpected, return the original
  };

  const calculateDays = (dateString: any) => {
    // Convert formatted date string to Date object
    const [day, month, year] = dateString.split("-");
    const transactionDate = new Date(`${year}-${month}-${day}T00:00:00`);
    const today = new Date();

    // Reset the time portion to ignore time differences
    const todayWithoutTime = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    // Calculate the difference in milliseconds
    const timeDiff = todayWithoutTime.getTime() - transactionDate.getTime();
    // Convert milliseconds to days
    const diffDays = Math.floor(timeDiff / (1000 * 3600 * 24));

    return isNaN(diffDays) ? 1 : diffDays + 1; // Return 0 days for the same day or NaN case
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800, // Adjust width as needed
          maxHeight: "80vh", // Limit the height of the modal
          bgcolor: "background.paper",
          border: "2px solid #000",
          boxShadow: 24,
          p: 4,
          overflowX: "auto", // Make the content scrollable on the Y-axis
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Transaction Details</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: "200px",
            borderTop: "1.5px solid rgba(224, 224, 224, 1)",
            padding: "0 20px",
            overflow: "auto", // Enable horizontal scrolling
            maxWidth: "100%", // Ensure the table doesn't exceed the container's width
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "start" }}>
                  Purchase Date
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Invested Amount
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "center" }}>
                  Withdrawal Amount
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", textAlign: "end" }}>
                  Days
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions?.map((trans: any, index: any) => (
                <TableRow key={index}>
                  <TableCell sx={{ textAlign: "start" }}>
                    {formatTransactionDate(trans?.transactionDate)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {trans?.investedAmount}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    {Number(trans?.redeemValue).toFixed(2) || 0}
                  </TableCell>
                  <TableCell sx={{ textAlign: "end" }}>
                    {calculateDays(
                      formatTransactionDate(trans?.transactionDate)
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Modal>
  );
};
const FDHoldingsTable: React.FC<Props> = ({
  data,
  totalPages,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
  fetchData,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState<FD[]>([]);
  const [loadingRows, setLoadingRows] = useState<number[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof FD | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [transModalOpen, setTransModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<FD | null>(null);
  const [modalMode, setModalMode] = useState<"edit" | "add" | "plus">("edit");
  const [selectedTransactions, setSelectedTransactions] = useState<[] | null>(
    null
  );
  // const location = useLocation();
  // let selectedData = location?.state?.selectedData||{};

  // console.log("this is current page", selectedData);

  console.log("this is current page", data);
  console.log("this is page size", pageSize);

  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };

  useEffect(() => {
    // Retrieve selectedData from session storage if available
    const storedData = sessionStorage.getItem("selectedData");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      if (!isEmptyObject(parsedData)) {
        setSelectedRow(parsedData); // Set selected data row
        setModalMode("plus"); // Set the mode to 'plus'
        setModalOpen(true); // Open the modal
      } else {
        setModalOpen(false);
        setSelectedRow(null);
      }
    }
  }, []);

  useEffect(() => {
    // Filter and sort data whenever the data or search term changes
    let updatedData = data;

    // Search functionality
    if (searchTerm) {
      updatedData = updatedData?.filter((row) =>
        Object.values(row).some(
          (value) =>
            value != null &&
            value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Sorting functionality
    if (sortColumn) {
      updatedData = [...updatedData].sort((a, b) => {
        if (a[sortColumn] < b[sortColumn]) return sortOrder === "asc" ? -1 : 1;
        if (a[sortColumn] > b[sortColumn]) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredData(updatedData);
  }, [data, searchTerm, sortColumn, sortOrder]);

  const handleSort = (column: keyof FD) => {
    setSortColumn(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  const handleEdit = (row: FD) => {
    setSelectedRow(row);
    setModalMode("edit");
    setModalOpen(true);
  };
  const handlePlus = (row: FD) => {
    setSelectedRow(row);
    setModalMode("plus");
    setModalOpen(true);
  };

  const handleModalClose = () => {
    sessionStorage.removeItem("selectedData");
    setSelectedRow(null);
    setModalOpen(false);
  };
  const handleCloseModal = () => {
    setTransModalOpen(false);
    setSelectedTransactions(null);
  };
  const handleModalSubmit = (data: FD) => {
    handleModalClose();
  };

  const handleOpenModal = (transactions: any) => {
    setSelectedTransactions(transactions);
    setTransModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedRow(null);
    setModalMode("add");
    setModalOpen(true);
  };
  function formatDate(dateString: any) {
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed in JavaScript
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }
  function convertDateFormat(dateStr: string) {
    // Split the date into MM/DD/YYYY parts
    const [day, month, year] = dateStr.split("/");

    // Return the date in DD-MM-YYYY format
    return `${day}-${month}-${year}`;
  }

  const convertDaysToReadableFormat = (days: number,schemeName:string): string => {
    if (schemeName && schemeName?.includes("HDFC")) {
      if (days <= 90) {
        return `${days} day${days > 1 ? "s" : ""}`;
      }
    } else {
      return days<=1?days +" month":days+" months" ;// Dynamic value for 12 months, approximated as 12 * 30 = 360 days
    }
    const today = new Date();
    let currentYear = today.getFullYear();
    let currentMonth = today.getMonth(); // Month is 0-based (0 = January, 11 = December)
    let currentDay = today.getDate();

    // Helper function to get the number of days in a given month and year
    const daysInMonth = (month: number, year: number): number => {
      return new Date(year, month + 1, 0).getDate();
    };

    // Determine the month length to use based on the current day
    let monthLength = daysInMonth(currentMonth, currentYear);
    if (currentDay > 20) {
      currentMonth += 1;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear += 1;
      }
      monthLength = daysInMonth(currentMonth, currentYear);
    }

    let totalMonths = 0;
    let remainingDays = days;
    let futureDate = new Date(today);

    // Calculate total months and remaining days
    while (remainingDays >= monthLength) {
      totalMonths += 1;
      remainingDays -= monthLength;
      currentMonth += 1;
      if (currentMonth > 11) {
        currentMonth = 0;
        currentYear += 1;
      }
      monthLength = daysInMonth(currentMonth, currentYear);
    }

    // Calculate the exact future date
    futureDate.setMonth(today.getMonth() + totalMonths);
    futureDate.setDate(today.getDate() + remainingDays);

    // Format the readable string
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    let readableFormat = "";
    if (years > 0) readableFormat += `${years} yr${years > 1 ? "s" : ""} `;
    if (months > 0) readableFormat += `${months} mon `;
    if (remainingDays > 0) readableFormat += `${remainingDays} day${remainingDays > 1 ? "s" : ""} `;

    return readableFormat.trim();
  };

  const pagedData = filteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  const sortHead = (label: string, column: keyof FD) => (
    <TableCell sx={centerHeadCellSx}>
      <SortableHeader label={label} onSort={() => handleSort(column)} align="center" />
    </TableCell>
  );

  return (
    <HoldingsPageShell
      title="Corporate FD"
      subtitle="View corporate fixed deposit holdings for this investor."
      searchValue={searchTerm}
      onSearchChange={(value) => {
        setSearchTerm(value);
        setPage(0);
      }}
      searchPlaceholder="Search corporate FD holdings..."
      action={<HoldingsAddButton onClick={handleAddNew} />}
    >
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {sortHead("Scheme Name", "schemeName")}
              {sortHead("Deposite Date", "createdAt")}
              {sortHead("Tenure", "investmentTenure")}
              <TableCell sx={centerHeadCellSx}>Maturity Date</TableCell>
              {sortHead("Invested Amount", "amount")}
              {sortHead("Interest P.A (%)", "interesetRate")}
              {sortHead("Interest Payment Frequency", "investmentFrequency")}
              <TableCell sx={centerHeadCellSx}>Interest Earneed</TableCell>
              {sortHead("Maturity Amount", "returnAmount")}
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedData.length === 0 ? (
              <TableRow>
                <EmptyTableRow colSpan={9} message="No corporate FD holdings found" />
              </TableRow>
            ) : (
              pagedData.map((row) => (
                <TableRow key={row.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                  <TableCell sx={centerBodyCellSx}>{row?.schemeName}</TableCell>
                  <TableCell sx={centerBodyCellSx}>{formatDate(row?.createdAt) || "-"}</TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {convertDaysToReadableFormat(row?.investmentTenure, row?.schemeName)}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {row?.endDate
                      ? /^\d{2}\/\d{2}\/\d{4}$/.test(row?.endDate.toString())
                        ? convertDateFormat(row.endDate.toString())
                        : convertDateFormat(
                            TimeStamptoExp.formatDateFromTimestamp(Number(row?.endDate))
                          )
                      : "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.amount).toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {Number(row?.interesetRate).toFixed(2) + "%" || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>{row?.investmentFrequency}</TableCell>
                  <TableCell sx={gainLossCellSx(row?.returnAmount - row?.amount)}>
                    {"Rs." + Number(row?.returnAmount - row?.amount).toFixed(2) || "-"}
                  </TableCell>
                  <TableCell sx={centerBodyCellSx}>
                    {"Rs." + Number(row?.returnAmount).toFixed(2) || "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={filteredData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onPageSizeChange}
                sx={holdingsPaginationSx}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <EditModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        rowData={
          selectedRow || {
            fdId: 0,
            id: 0,
            noteficationId: 0,
            schemeName: "",
            providerName: "",
            category: "",
            amount: 0,
            fdStatus: 0,
            transactionDate: "",
            investmentFrequency: "",
            interesetRate: 0,
            grossInterest: 0,
            transactions: [],
            endDate: 0,
            investmentTenure: 0,
            returnAmount: 0,
            createdAt: "",
            type: "",
            daysLeft: 0,
            userId: 0,
            startDate: 0,
            name: "",
            firstName: "",
            folioNo: "",
          }
        }
        filteredData={filteredData}
        mode={modalMode}
        fetchData={fetchData}
      />
      {selectedTransactions && (
        <TransactionsModal
          open={transModalOpen}
          onClose={handleCloseModal}
          transactions={selectedTransactions}
        />
      )}
    </HoldingsPageShell>
  );
};

export default FDHoldingsTable;
